import React, { useState } from 'react';
import { Notification } from '@Types/notification';
import classNames from 'classnames';
import { useAccount } from '@Provider/frontastic';
import Spinner from 'components/commercetools-ui/spinner';
import TrashIcon from '../../../../icons/trash';
import ToggleElement from '../../../toggle-element';
import DeleteConfirmationModal from '../modals/delete-confirmation';

type NotificationItemProps = {
  entry: Notification;
};

const NotificationItem: React.FC<NotificationItemProps> = ({ entry }) => {
  const { deleteNotification, updateNotification } = useAccount();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const date = new Date(entry.createdAt).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

  const deleteNotificationHandler = async () => {
    await deleteNotification({
      id: entry.id,
    });
  };

  const handleToggle = async (id: string, toggle: boolean) => {
    setLoading(true);

    try {
      await updateNotification({
        id: id,
        active: toggle,
      });
    } catch (e) {
      console.error('Error while updating notification.');
    } finally {
      setLoading(false);
    }
  };

  const openDeleteModal = () => setDeleteModalOpen(true);

  const closeDeleteModal = () => setDeleteModalOpen(false);

  return (
    <div className="relative flex w-full flex-col gap-4 rounded bg-neutral-50 p-4 shadow-sm lg:border lg:border-neutral-200 lg:bg-inherit">
      {loading && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/50">
          <Spinner />
        </div>
      )}
      <div className="basis-full">
        <div className="flex items-start justify-between">
          <span className="text-md ">{entry.title}</span>
          <button onClick={openDeleteModal} className="cursor-pointer">
            <TrashIcon className="size-6" aria-hidden="true" />
          </button>
        </div>
        <span className="mt-0.5 text-[11px]">Erstellt am {date}</span>
      </div>
      <div className="flex flex-col gap-4">
        <div className="flex basis-full items-center gap-2">
          <ToggleElement
            id={entry.id}
            onChange={handleToggle}
            initial={entry.active}
            label="Benachrichtigungen"
            enableStateLabel="aktiviert"
            disableStateLabel="deaktiviert"
          />
        </div>
        <div>
          <a
            href={entry.resultUrl}
            target="_blank"
            className={classNames(
              !entry.active
                ? 'pointer-events-none bg-neutral-200 text-neutral-500'
                : 'bg-neutral-950 text-white hover:bg-neutral-900 focus:bg-neutral-900',
              'inline-flex items-center justify-center rounded-4xl px-8 py-2.5 text-md font-semibold transition duration-150 ease-out',
            )}
          >
            Zu den Ergebnissen
          </a>
        </div>
      </div>
      <DeleteConfirmationModal
        open={deleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={deleteNotificationHandler}
      />
    </div>
  );
};

export default NotificationItem;
